import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getCollections } from "services/storefront/common";
import Link from "helpers/link";
import { ReactComponent as CopyURL } from "assets/svgs/copy-url.svg";
import Spinner from "components/spinner";
import SelectWithSearch from "components/select-with-search";
import ProductFinder from "@root/components/product-finder";


const ProductCreator = ({ data }) => {
    const [collections, setCollections] = useState({
        data: [], selected: { name: null, value: null }, versionInput: "", ymmInput: "", year: "", make: "", model: "", option: "",
    });
    const [error, setError] = useState(null);
    const [url, setUrl] = useState("");


    const fetchCollections = async () => {
        const shopifyCollections = await getCollections(data.collectionItems.map((item) => item.handle));
        const dataArray = Object.values(shopifyCollections.data || {});

        const dataObject = dataArray.reduce((acc, item, index) => {
            const key = item.handle;
            acc[key] = {
                ...item,
                title: data.collectionItems[index].title,
                url: data.collectionItems[index].url,
            };
            return acc;
        }, {});

        setCollections((prevState) => ({
            ...prevState,
            data: dataObject,
            selected: {
                name: shopifyCollections.data.collection0.handle,
                value: shopifyCollections.data.collection0.products.edges[0].node.handle,
            },
        }));
    };

    const handleCollectionChange = (event) => {
        const { name, value } = event.target;

        setUrl("");
        setError(null);

        if (name === "store") {
            return setCollections((prevState) => ({
                ...prevState,
                ymmInput: "",
                selected: { name: value, value: collections.data[value].products.edges[0].node.handle },
            }));
        }
        if (name === "product") {
            return setCollections((prevState) => ({
                ...prevState,
                ymmInput: "",
                selected: { ...prevState.selected, value },
            }));
        }
        if (name === "versionInput") {
            return setCollections(((prevState) => ({
                ...prevState,
                versionInput: value,
            })));
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (showProductFinder()) {
            // validate year, make, model and add error separately, and show separate error message
            if (!collections.make) {
                return setError("Please select a make.");
            }
            if (!collections.model) {
                return setError("Please select a model.");
            }
            if (!collections.year) {
                return setError("Please select a year.");
            }
        }

        setError(null);

        const versionComplete = [collections.ymmInput, collections.versionInput].filter(Boolean).join(" | ");
        const versionInputWithDash = versionComplete.replace(/ /g, '-');

        if (collections.data[collections.selected.name].url === "/") {
            const storeUrl = `${window.location.origin}/products/${collections.selected.value}?version_${versionInputWithDash}`;

            navigator.clipboard.writeText(storeUrl)

            return setUrl(storeUrl);
        }

        const storeUrl = `${collections.data[collections.selected.name].url}/products/${collections.selected.value}?version_${versionInputWithDash}`;

        navigator.clipboard.writeText(storeUrl);

        return setUrl(storeUrl);
    };


    useEffect(() => {
        fetchCollections();
    }, []);

    let ymmChange = useCallback((data) => {
        setUrl("");
        setError(null);

        setCollections((prevState) => ({
            ...prevState,
            ymmInput: [data?.year?.selected, data?.make?.selected, data?.model?.selected, data?.option?.selected].filter(Boolean).join(" "),
            year: data?.year?.selected,
            make: data?.make?.selected,
            model: data?.model?.selected,
            option: data?.option?.selected,
        }));
    }, [setCollections]);

    const productOptions = useMemo(() => {
        const edges = collections?.data?.[collections?.selected?.name]?.products?.edges || [];
        const cleanLabel = (value) => value
            .replace(/\b(Seal|Skin|Covers)\b/gi, "")
            .replace(/\s+/g, " ")
            .trim();
        return edges.map((product) => {
            const productCreatorTitle = product.node.productCreatorTitle?.value;
            const metaLabel = product.node.metafield?.value;
            const cleanedTitle = cleanLabel(product.node.title);
            const priceAmount = product.node.priceRange?.minVariantPrice?.amount;
            const priceSuffix = priceAmount ? ` - $${priceAmount}` : "";
            const label = productCreatorTitle
                ? `${cleanLabel(productCreatorTitle)}${priceSuffix}`
                : metaLabel
                    ? metaLabel
                    : `${cleanedTitle}${priceSuffix}`;
            return {
                value: product.node.handle,
                label,
            };
        });
    }, [collections.data, collections.selected.name]);

    if (collections.data.length === 0) return <Spinner />;

    function getCollectionType(handle) {
        const item = collections.data[collections.selected.name]?.products?.edges?.find(edge => {
            return edge.node.handle === handle;
        });

        const itemValue = item?.node?.productCreatorTitle?.value
            || item?.node?.metafield?.value
            || item?.node?.title
            || null;

        if (!itemValue) return null;

        const lowerHandle = itemValue.toLowerCase();
        if (lowerHandle.includes("pontoon")) return "pontoon";
        if (lowerHandle.includes("bimini top")) return "bimini";
        if (lowerHandle.includes("jetski")) return "jet_ski";
        if (lowerHandle.includes("jet ski")) return "jet_ski";
        if (lowerHandle.includes("rv -")) return "rv";
        if (lowerHandle.includes("limo -")) return "limo";
        if (lowerHandle.includes("motorcycle")) return "motorcycle";
        if (lowerHandle.includes("scooter")) return "scooter";
        if (lowerHandle.includes("atv")) return "atv";
        if (lowerHandle.includes("utv")) return "utv";
        if (lowerHandle.includes("snowmobile")) return "snowmobile";
        if (lowerHandle.includes("boat")) return "boat";
        if (lowerHandle.includes("stand up") || lowerHandle.includes("walkthrough")) return "boat";
        if (lowerHandle.includes("suv")) return "car";
        if (lowerHandle.includes("truck")) return "car";
        if (lowerHandle.includes("van")) return "car";
        if (lowerHandle.includes("car")) return "car";
        // Boats store: default remaining products to boat YMM
        if (collections.selected.name === "quick-product-creator-boats") return "boat";
        return null;
    }

    function showProductFinder() {
        let type = getCollectionType(collections.selected.value);
        return (collections.selected.name === "quick-product-creator" || collections.selected.name === "quick-product-creator-boats") && type !== "rv" && type !== "limo";
    }

    return (
        <div>
            <h2 className="text-center text-blue-700 mb-8"> Quick Product Creator </h2>
            <form
                onSubmit={handleSubmit}
                onChange={handleCollectionChange}
                className="max-w-[500px] mx-auto"
            >
                <label className="flex flex-col form-label" htmlFor="store">
                    Store
                    <select className="form-input" name="store">
                        {Object.values(collections?.data || {})?.map((collection) => (
                            <option key={collection.handle} value={collection.handle}>
                                {collection.title}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="flex flex-col form-label">
                    Product
                    <SelectWithSearch
                        name="product"
                        value={collections?.selected?.value}
                        options={productOptions}
                        onChange={handleCollectionChange}
                        placeholder="Select a product"
                        searchPlaceholder="Search products..."
                        fuzzyThreshold={0.7}
                    />
                </label>

                {showProductFinder() && (
                    <div className="ml-[-300px] mr-[-300px] mb-2">
                        <ProductFinder
                            key={getCollectionType(collections.selected.value)}
                            newFinder={true}
                            onChange={ymmChange}
                            data={{
                                collectionType: getCollectionType(collections.selected.value),
                                backgroundColor: 'lightgray',
                                isQuickProductCreator: true,
                                bgImage: null
                            }}
                        />
                    </div>
                )}

                <label className="flex flex-col form-label" htmlFor="product">
                    Version
                    <div className="version-input__wrapper">
                        {showProductFinder() && <span>{collections.ymmInput}</span>}
                        <input
                            name="versionInput"
                            className="form-input version-input w-full"
                            type="text"
                            defaultValue={collections?.versionInput}
                        />
                    </div>
                </label>
                <button className="btn-primary w-1/3 font-normal" type="submit">
                    Create and Copy
                </button>

                {error && <div className="text-red-600 mb-4">{error}</div>}

                {url && (
                    <div className="mt-4 flex flex-col">
                        <Link className="text-gray-601 mb-4 tracking-wider" to={url}>
                            {url}
                        </Link>
                        <button
                            type="button"
                            className="flex items-center gap-2 text-blue-700 font-light"
                            onClick={() => navigator.clipboard.writeText(url)}
                        >
                            <CopyURL className="w-5 h-5" />
                            Copy Above URL
                        </button>
                    </div>
                )}
            </form>

            <style>{`
                .version-input__wrapper {
                    border: 1px solid rgb(228 231 233);
                    display: flex;
                    white-space: nowrap;
                    
                    padding-top: 0.625rem;
                    padding-bottom: 0.625rem;
                    padding-left: 0.7rem;
                    padding-right: 0.7rem;
                }
                .version-input__wrapper:focus-within {
                    outline: #254DEB solid 1px;
                }
                .version-input__wrapper span {
                    display: inline-flex;
                    padding: 0.1em 0.4em;
                    background-color: #f3f4f6;
                    border-radius: 0.375rem;
                    font-size: 0.875rem;
                }
                .version-input {
                    border: none;
                    padding: 0;
                    height: unset;
                    padding-left: 0.4rem;
                }
                .version-input:focus {
                    outline: none;
                }
            `}</style>

        </div>
    );
};

export default ProductCreator;
